/**
 * CompanyTeamPanel — Iter-313b · Client Settings > Team section.
 *
 * Combined UI for the two lists (Team Members + Pending Invitations)
 * plus owner-only actions:
 *   • Invite Admin/Member/Viewer with role select modal.
 *   • Resend / Revoke pending invites.
 *   • Change role (Admin | Member | Viewer) inline via select.
 *   • Remove member with a shadcn Dialog confirmation.
 *
 * Backend surface (routes/company_users_routes.py):
 *   GET  /api/company/users
 *   POST /api/company/users/set-role
 *   POST /api/company/users/remove
 *   GET  /api/company/invites
 *   POST /api/company/invites
 *   POST /api/company/invites/{id}/resend
 *   DELETE /api/company/invites/{id}
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Loader2, Crown, Shield, User as UserIcon, Eye, UserPlus, UserMinus,
  Mail, RefreshCw, XCircle, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const ROLE_META = {
  owner:  { label: 'Owner',  cls: 'bg-amber-500/15 text-amber-200 border-amber-500/40', icon: Crown },
  admin:  { label: 'Admin',  cls: 'bg-cyan-500/20 text-cyan-200 border-cyan-500/40',    icon: Shield },
  member: { label: 'Member', cls: 'bg-zinc-100 text-zinc-700 border-zinc-200',    icon: UserIcon },
  viewer: { label: 'Viewer', cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Eye },
};

const STATUS_META = {
  pending:  { label: 'Pending',  cls: 'bg-amber-500/10 text-amber-300 border-amber-500/40' },
  accepted: { label: 'Accepted', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  expired:  { label: 'Expired',  cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  revoked:  { label: 'Revoked',  cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const RoleBadge = ({ role }) => {
  const m = ROLE_META[role] || ROLE_META.member;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`${m.cls} text-[11px]`} data-testid={`team-role-badge-${role}`}>
      <Icon className="w-3 h-3 mr-1" /> {m.label}
    </Badge>
  );
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <Badge variant="outline" className={`${m.cls} text-[11px]`} data-testid={`invite-status-${status}`}>
      {m.label}
    </Badge>
  );
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const CompanyTeamPanel = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [sending, setSending] = useState(false);

  const [removeTarget, setRemoveTarget] = useState(null); // user object

  const load = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.get('/company/users'),
        api.get('/company/invites'),
      ]);
      setData(u.data);
      setInvites(i.data?.invites || []);
      setIsOwner(!!u.data?.is_current_user_owner);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changeRole = async (user, newRole) => {
    if (newRole === user.company_role) return;
    setBusyId(user.id);
    try {
      await api.post('/company/users/set-role', { user_id: user.id, role: newRole });
      toast.success(`${user.display_name} is now ${ROLE_META[newRole]?.label || newRole}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to change role');
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setBusyId(removeTarget.id);
    try {
      await api.post('/company/users/remove', { user_id: removeTarget.id });
      toast.success(`${removeTarget.display_name} detached from company`);
      setRemoveTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to remove user');
    } finally {
      setBusyId(null);
    }
  };

  const sendInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setSending(true);
    try {
      const res = await api.post('/company/invites', { email, role: inviteRole });
      toast.success(`Invite sent to ${email}`);
      // Auto-copy the accept URL as a fallback for slow email delivery
      if (res.data?.accept_url) {
        try { await navigator.clipboard.writeText(res.data.accept_url); } catch (_) { /* ignore */ }
      }
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('admin');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  const resendInvite = async (invite) => {
    setBusyId(invite.id);
    try {
      await api.post(`/company/invites/${invite.id}/resend`);
      toast.success(`Invite resent to ${invite.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvite = async (invite) => {
    setBusyId(invite.id);
    try {
      await api.delete(`/company/invites/${invite.id}`);
      toast.success('Invite revoked');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to revoke invite');
    } finally {
      setBusyId(null);
    }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success('Invite link copied'); }
    catch (_) { toast.error('Copy failed — link: ' + url); }
  };

  if (loading) {
    return (
      <Card className="bg-slate-900/60 border-slate-800/60 text-slate-100 backdrop-blur-sm">
        <CardContent className="p-8 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
        </CardContent>
      </Card>
    );
  }

  const users = data?.users || [];

  return (
    <div className="space-y-4" data-testid="company-team-panel">
      {/* Header + Invite button */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-slate-100">Team</h2>
          <p className="text-xs text-slate-400">
            Manage who has access to your company on iEvntür.
          </p>
        </div>
        {isOwner && (
          <Button
            onClick={() => setInviteOpen(true)}
            className="bg-cyan-500/150 hover:bg-cyan-600 text-white"
            size="sm"
            data-testid="open-invite-admin-dialog"
          >
            <UserPlus className="w-4 h-4 mr-1" /> Invite Admin
          </Button>
        )}
      </div>

      {/* Team members */}
      <Card className="bg-slate-900/60 border-slate-800/60 text-slate-100 backdrop-blur-sm">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-cyan-400" />
            Team Members ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y divide-slate-800">
          {users.length === 0 && (
            <p className="p-6 text-sm text-slate-400" data-testid="team-empty">
              No teammates yet. Send your first invite above.
            </p>
          )}
          {users.map((u) => (
            <div
              key={u.id}
              className="px-4 py-3 flex items-start justify-between gap-3"
              data-testid={`team-row-${u.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-slate-100 truncate" data-testid={`team-name-${u.id}`}>
                    {u.display_name}
                  </span>
                  <RoleBadge role={u.company_role} />
                </div>
                <p className="text-[11px] text-slate-400">
                  {u.email}
                  {u.last_login_at ? ` · last active ${fmt(u.last_login_at)}` : ''}
                </p>
              </div>
              {isOwner && u.company_role !== 'owner' && (
                <div className="flex items-center gap-2 shrink-0">
                  <Select
                    value={u.company_role}
                    onValueChange={(v) => changeRole(u, v)}
                    disabled={busyId === u.id}
                  >
                    <SelectTrigger
                      className="h-8 w-[120px] text-xs bg-slate-950/60 border-slate-700 text-slate-100"
                      data-testid={`role-select-${u.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm" variant="outline"
                    disabled={busyId === u.id}
                    onClick={() => setRemoveTarget(u)}
                    className="text-rose-400 hover:bg-rose-500/10"
                    data-testid={`remove-user-${u.id}`}
                  >
                    <UserMinus className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Pending / historical invites */}
      <Card className="bg-slate-900/60 border-slate-800/60 text-slate-100 backdrop-blur-sm">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-400" />
            Invitations ({invites.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 divide-y divide-slate-800">
          {invites.length === 0 && (
            <p className="p-6 text-sm text-slate-400" data-testid="invites-empty">
              No invites sent yet.
            </p>
          )}
          {invites.map((inv) => (
            <div
              key={inv.id}
              className="px-4 py-3 flex items-start justify-between gap-3"
              data-testid={`invite-row-${inv.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <span className="text-sm font-medium text-slate-100 truncate">{inv.email}</span>
                  <StatusBadge status={inv.status} />
                  {inv.org_role && <RoleBadge role={inv.org_role} />}
                </div>
                <p className="text-[11px] text-slate-400">
                  sent by {inv.sent_by_email || '—'} · {fmt(inv.sent_at)}
                  {inv.status === 'pending' && inv.expires_at
                    ? ` · expires ${fmt(inv.expires_at)}`
                    : inv.status === 'accepted' && inv.accepted_at
                      ? ` · accepted ${fmt(inv.accepted_at)}`
                      : ''}
                </p>
              </div>
              {isOwner && inv.status === 'pending' && (
                <div className="flex items-center gap-1 shrink-0">
                  {inv.accept_url && (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => copyLink(inv.accept_url)}
                      data-testid={`copy-invite-${inv.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    size="sm" variant="outline"
                    disabled={busyId === inv.id}
                    onClick={() => resendInvite(inv)}
                    className="text-cyan-300 hover:bg-cyan-500/15"
                    data-testid={`resend-invite-${inv.id}`}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Resend
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    disabled={busyId === inv.id}
                    onClick={() => revokeInvite(inv)}
                    className="text-rose-400 hover:bg-rose-500/10"
                    data-testid={`revoke-invite-${inv.id}`}
                  >
                    <XCircle className="w-3 h-3 mr-1" /> Revoke
                  </Button>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Invite modal */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="invite-admin-dialog">
          <DialogHeader>
            <DialogTitle>Invite a teammate</DialogTitle>
            <DialogDescription>
              They&apos;ll receive an email with a 7-day accept link. Admins can post events and manage staffing.
              Members can view; Viewers are read-only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@company.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — post/manage events + staffing</SelectItem>
                  <SelectItem value="member">Member — view + manage assigned events</SelectItem>
                  <SelectItem value="viewer">Viewer — read-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button
              onClick={sendInvite}
              disabled={sending || !inviteEmail.trim()}
              className="bg-cyan-500/150 hover:bg-cyan-600 text-white"
              size="sm"
              data-testid="send-invite-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Mail className="w-4 h-4 mr-1" />}
              Send Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation dialog */}
      <Dialog open={!!removeTarget} onOpenChange={(v) => { if (!v) setRemoveTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="remove-user-dialog">
          <DialogHeader>
            <DialogTitle>Remove {removeTarget?.display_name}?</DialogTitle>
            <DialogDescription>
              They&apos;ll be detached from your company and will lose access to your events, invoices, and settings.
              Historical shifts and invoices remain with your company for audit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button
              onClick={confirmRemove}
              disabled={busyId === removeTarget?.id}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-remove-user"
            >
              {busyId === removeTarget?.id
                ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                : <UserMinus className="w-4 h-4 mr-1" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyTeamPanel;
